import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { authApi } from '../../authApi';
import { AUTH_ROUTES } from '../../authConstants';
import {
  isResetPasswordFormValid,
  validateResetPasswordForm,
  type ResetPasswordFormErrors,
} from '../../authValidation';

const I18N = 'auth.resetPassword';

/** Delay (ms) before the success screen auto-redirects to the login page. */
const REDIRECT_DELAY_MS = 3000;

// AuthCard forces `text.body` on every Typography (theme text.primary is white,
// for the branded background). Muted copy needs an equal-specificity override.
const MUTED = { '&.MuiTypography-root': { color: 'text.label' } } as const;

interface ResetPasswordFormProps {
  /** Opaque reset token from the emailed link (`/reset-password?token=…`). */
  token: string;
}

/** Back-to-login link (arrow + label). */
function BackToLogin() {
  const { t } = useTranslation('common');
  return (
    <Link
      component={RouterLink}
      to={AUTH_ROUTES.LOGIN}
      underline="hover"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        fontWeight: 'fontWeightMedium',
      }}
    >
      <ArrowBackIcon fontSize="small" />
      {t(`${I18N}.backToLogin`)}
    </Link>
  );
}

export function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<ResetPasswordFormErrors>({});
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // On success the account is usable immediately — send the user to login.
  useEffect(() => {
    if (!done) return;
    const id = setTimeout(() => navigate(AUTH_ROUTES.LOGIN, { replace: true }), REDIRECT_DELAY_MS);
    return () => clearTimeout(id);
  }, [done, navigate]);

  const submit = async () => {
    setLoading(true);
    setServerError(undefined);
    try {
      await authApi.resetPassword({ token, newPassword: password });
      setDone(true);
    } catch {
      // The form already enforces the same password policy as the backend, so a
      // reset failure here is a token problem (expired / invalid / already used).
      setServerError(t(`${I18N}.expiredError`));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    const nextErrors = validateResetPasswordForm({ password, confirmPassword });
    setErrors(nextErrors);
    if (!isResetPasswordFormValid(nextErrors)) return;
    void submit();
  };

  // ── Success state ─────────────────────────────────────────────────────────
  if (done) {
    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}
      >
        <Box
          sx={theme => ({
            width: theme.avatar.lg,
            height: theme.avatar.lg,
            borderRadius: theme.borderRadius.pill,
            mb: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.success.main, 0.14),
            color: 'success.main',
          })}
        >
          <CheckCircleOutlineIcon />
        </Box>

        <Typography variant="h5" component="h1" sx={{ fontWeight: 'fontWeightMedium', mb: 1 }}>
          {t(`${I18N}.successTitle`)}
        </Typography>
        <Typography variant="body2" sx={MUTED}>
          {t(`${I18N}.successMessage`)}
        </Typography>

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={() => navigate(AUTH_ROUTES.LOGIN, { replace: true })}
          sx={{ mt: 3, mb: 2 }}
        >
          {t(`${I18N}.goToLogin`)}
        </Button>
      </Box>
    );
  }

  // ── Form state ────────────────────────────────────────────────────────────
  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 'fontWeightMedium', mb: 1 }}>
        {t(`${I18N}.title`)}
      </Typography>
      <Typography variant="body2" sx={MUTED}>
        {t(`${I18N}.subtitle`)}
      </Typography>

      {serverError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {serverError}
        </Alert>
      )}

      <TextField
        label={t(`${I18N}.fields.password`)}
        name="password"
        type={showPassword ? 'text' : 'password'}
        autoComplete="new-password"
        autoFocus
        fullWidth
        required
        margin="normal"
        value={password}
        onChange={e => {
          setPassword(e.target.value);
          setErrors(prev => ({ ...prev, password: undefined }));
          setServerError(undefined);
        }}
        error={Boolean(errors.password)}
        helperText={errors.password ? t(errors.password) : t(`${I18N}.passwordHint`)}
        disabled={loading}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t(showPassword ? `${I18N}.hidePassword` : `${I18N}.showPassword`)}
                  onClick={() => setShowPassword(v => !v)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      <TextField
        label={t(`${I18N}.fields.confirmPassword`)}
        name="confirmPassword"
        type={showConfirm ? 'text' : 'password'}
        autoComplete="new-password"
        fullWidth
        required
        margin="normal"
        value={confirmPassword}
        onChange={e => {
          setConfirmPassword(e.target.value);
          setErrors(prev => ({ ...prev, confirmPassword: undefined }));
          setServerError(undefined);
        }}
        error={Boolean(errors.confirmPassword)}
        helperText={errors.confirmPassword ? t(errors.confirmPassword) : undefined}
        disabled={loading}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t(showConfirm ? `${I18N}.hidePassword` : `${I18N}.showPassword`)}
                  onClick={() => setShowConfirm(v => !v)}
                  edge="end"
                  size="small"
                >
                  {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={theme.iconSize.sm} color="inherit" /> : null}
        sx={{ mt: 3, mb: 2 }}
      >
        {loading ? t(`${I18N}.submitting`) : t(`${I18N}.submit`)}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <BackToLogin />
      </Box>
    </Box>
  );
}
